import { useCallback, useState } from 'react';
import { useFlagsService } from '@atlassian/jira-flags';
import usePortalGroupsResource from '@atlassian/jira-servicedesk-request-types-config-common/src/controllers/portal-groups/index.tsx';
import type { RemoveGroupGuard, RemoveConfirmation } from '../../common/types';
import { DELETE_GROUP } from '../../common/ui/flags/constants.tsx';
import getErrorFlag from '../../common/ui/flags/main.tsx';
import { useActionsProvider } from '../../services/store';
import useToggleState from '../toggle-state';
import useTracking from '../tracking';

const useRemoveGroup = (groupId: number) => {
	const [isLoading, toggleLoading] = useToggleState();
	const [confirmation, setConfirmation] = useState<RemoveConfirmation>();
	const { removeGroup, getRemoveGroupGuard } = useActionsProvider();
	const { fireError, fireTrack } = useTracking();
	const { showFlag } = useFlagsService();
	const { clear: clearPortalGroupsResourceCache } = usePortalGroupsResource();

	const remove = useCallback(() => {
		toggleLoading();
		removeGroup(groupId, (_, error) => {
			if (error) {
				toggleLoading();
				fireError('removeGroup', error, { groupId });
				showFlag(getErrorFlag(DELETE_GROUP, error));
			} else {
				fireTrack('portalGroup removed', { groupId });
				clearPortalGroupsResourceCache();
			}
		});
	}, [
		clearPortalGroupsResourceCache,
		fireError,
		fireTrack,
		groupId,
		removeGroup,
		showFlag,
		toggleLoading,
	]);

	const confirmRemove = useCallback(
		(guardOverride?: RemoveGroupGuard) => {
			const guard = guardOverride || getRemoveGroupGuard(groupId);

			if (!guard) {
				remove();
				return;
			}

			setConfirmation({
				details: guard,
				onClose: () => {
					setConfirmation(undefined);
				},
				onConfirm: () => {
					setConfirmation(undefined);
					remove();
				},
			});
		},
		[getRemoveGroupGuard, groupId, remove],
	);

	return [confirmation, isLoading, confirmRemove] as const;
};

export default useRemoveGroup;
